import React from 'react';
import { useTranslation } from 'react-i18next';

export default function QuickViewModal({ product, isOpen, onClose, onAddToCart }) {
  const { t, i18n } = useTranslation();
  
  // Close on escape key - MUST be before early return to follow React Hook rules
  React.useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose();
    };
    
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }
    
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]); // Removed onClose from deps to prevent infinite loop

  if (!isOpen || !product) return null;

  const isSoldOut = product.quantity === 0;

  const handleAddToCart = () => {
    onAddToCart(product);
    // Close modal after adding to cart
    onClose();
  };

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
      
      {/* Modal */}
      <div 
        className="relative bg-raya-card rounded-2xl shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-10 p-2 rounded-full bg-raya-bg/90 backdrop-blur-sm hover:bg-raya-accent-light transition-all transform hover:scale-110"
          aria-label="Close"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        <div className="grid md:grid-cols-2 gap-6 p-6 md:p-8 h-full">
          {/* Left: Product Image */}
          <div className="relative max-h-[calc(90vh-4rem)]">
            <div className="bg-raya-bg-off rounded-xl overflow-hidden h-full flex items-center justify-center">
              {product.image ? (
                <img
                  src={product.image}
                  alt={product.name}
                  className="w-full h-full object-contain"
                />
              ) : (
                <span className="text-8xl">📦</span>
              )}
              
              {isSoldOut && (
                <div className="absolute inset-0 bg-black bg-opacity-40 flex items-center justify-center">
                  <div className="bg-red-600 text-white px-8 py-4 rounded-lg transform -rotate-45 scale-125">
                    <span className="text-3xl font-bold whitespace-nowrap">{t('card.soldOut')}</span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Right: Product Details */}
          <div className="flex flex-col max-h-[calc(90vh-4rem)] overflow-y-auto">
            {/* Product Name */}
            <h2 className="text-3xl font-bold mb-3 text-raya-text-primary">
              {i18n.language === 'am' && product.name_am 
                ? product.name_am 
                : t(`products.${product.name}.name`, { defaultValue: product.name })}
            </h2>

            {/* Price */}
            <div className="mb-4">
              <span className="text-4xl font-bold bg-gradient-to-r from-raya-accent to-raya-accent-hover bg-clip-text text-transparent">
                ${product.price}
              </span>
            </div>

            {/* Stock Status */}
            <div className="mb-6 flex items-center gap-2">
              <span className={`font-semibold ${product.quantity > 0 ? 'text-green-600' : 'text-red-600'}`}>
                {product.quantity > 0 ? `${product.quantity} available` : t('card.soldOut')}
              </span>
            </div>

            {/* Divider */}
            <div className="border-t border-raya-border mb-6" />

            {/* Description */}
            <div className="mb-6 flex-1">
              <h3 className="text-lg font-semibold mb-2 text-raya-text-primary">
                Description
              </h3>
              <p className="text-raya-text-secondary leading-relaxed">
                {i18n.language === 'am' && product.description_am 
                  ? product.description_am 
                  : t(`products.${product.name}.description`, { defaultValue: product.description })}
              </p>
            </div>

            {/* Product Details */}
            <div className="mb-6 bg-raya-bg-off rounded-lg p-4">
              <h3 className="text-lg font-semibold mb-3 text-raya-text-primary">
                Product Details
              </h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-raya-text-secondary">SKU:</span>
                  <span className="font-medium">{product.id}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-raya-text-secondary">{t('card.stock')}:</span>
                  <span className="font-medium">{product.quantity}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-raya-text-secondary">Availability:</span>
                  <span className={`font-medium ${product.quantity > 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {product.quantity > 0 ? 'In Stock' : t('card.soldOut')}
                  </span>
                </div>
              </div>
            </div>

            {/* Add to Cart Button */}
            <button
              onClick={handleAddToCart}
              disabled={isSoldOut}
              className={`w-full py-4 rounded-xl font-semibold text-lg transition-all transform ${
                isSoldOut
                  ? 'bg-zinc-700 cursor-not-allowed text-zinc-400'
                  : 'bg-gradient-to-r from-raya-accent to-raya-accent-hover hover:from-raya-accent-hover hover:to-raya-accent text-raya-bg active:scale-95 shadow-lg hover:shadow-xl'
              }`}
            >
              {isSoldOut ? t('card.soldOut') : t('card.addToCart')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
